import React, { useRef, useEffect, useState } from 'react';
import logger from '@gral/log';

import { Theme, useTheme } from '@mui/material/styles';
import { useSetState } from 'react-use';
//@ts-ignore
import newPoint from './locationNew.svg';
//@ts-ignore
import otherPoint from './location.svg';

import OlMap from 'ol/Map';
import OlStyle from 'ol/style/Style';
import OlStroke from 'ol/style/Stroke';
import OlText from 'ol/style/Text';
import OlFill from 'ol/style/Fill';
import OlLayer from 'ol/layer/Layer.js';
import OlStyleIcon from 'ol/style/Icon';
import { Translate as OlTranslate } from 'ol/interaction';
import OlFeature from 'ol/Feature';
import OlInteractionDraw from 'ol/interaction/Draw';
import OlInteractionModify from 'ol/interaction/Modify';
import OlLayerVector from 'ol/layer/Vector';
import OlSourceVector from 'ol/source/Vector';
import * as OlProj from 'ol/proj';
import { Polygon, Point } from 'ol/geom';
import { Coordinate } from 'ol/coordinate';
import { transform } from 'ol/proj';
import * as OlExtent from 'ol/extent';
import QuickMap from './QuickMap';
import { CView, CGeometry, IGeometry, CElement, IForm } from '@ts/ui-models';
import { fetchData } from '@redux/slices/functionals/request/requestActions';
import { useAppDispatch } from '@redux/store';
import Collection from 'ol/Collection';
import TNotifs from '@components/TNotifs';
import { TTextType } from '@ts/storybook';

const GPS_COORDS = 'geomGpsCoord';
const LOCAL_COORDS = 'geomLocalRectCoord';
const LAYER_OTHER_GEO = 'layer_other_geometries';

interface GeometryDrawerProps extends IForm {
  element?: CElement; //Draw element no location
  geometry?: CGeometry; //If not geometry, draw otherGeometries
  disabled?: boolean; // Disable the drawer like read only
  othersGeometries?: string[]; //Ids of other location need to have geometries
  onChangeGeometry?: (key: 'geomGpsCoord' | 'geomLocalRectCoord', value: any) => void;
  hideInfoSideBar?: boolean;
  isQuickAccess?: boolean;
}

const tList3857 = (v: any) => v && v.map(coord => transform(coord, 'EPSG:4326', 'EPSG:3857'));
const t3857 = (v: any) => v && transform(v, 'EPSG:4326', 'EPSG:3857');
const ID_LAYER_DRAWER = 'layer_geometry_drawer';
const log = logger(`GeometryDrawer`);

const GeometryDrawer = (props: GeometryDrawerProps) => {
  const dispatch = useAppDispatch();
  const theme: Theme = useTheme();
  const tGeometry = props.geometry;

  const [state, setState] = useState<{ isLoading: boolean; othersGeometries: IGeometry[] | null; tElement?: CElement }>({
    isLoading: true,
    othersGeometries: null,
    tElement: props.element,
  });
  const [stateMap, setStateMap] = useSetState<{ source: any; currentFeature: any }>({ source: null, currentFeature: null });
  const [olmap, setOlMap] = useState<OlMap | null>(null);
  const [alert, setAlert] = useState<TTextType | null>(null);
  const [view, setView] = useState<CView | null>(null);

  const isGPS = view?.isWorldView() ?? true;
  const keyCoords = isGPS ? GPS_COORDS : LOCAL_COORDS;

  const [drawer, setDrawer] = useState<{
    draw: OlInteractionDraw | null;
    modify: OlInteractionModify | null;
    translate: OlTranslate | null;
  }>({
    draw: null,
    modify: null,
    translate: null,
  });
  const previousValueRef = useRef<CGeometry | null>(null);
  const mapRef = useRef<OlMap | null>(null);

  //#region Handlers
  const onOlmapChange = (olmap: OlMap, view: CView) => {
    setStateMap({ source: null, currentFeature: null });
    setView(view);
    setOlMap(olmap);
  };
  const addOrModifyFeature = coords => {
    if (coords && coords[0] && coords[0].length > 0) {
      coords = isGPS ? coords[0].map(coord => OlProj.toLonLat(coord)) : coords[0];
      props.onChangeGeometry?.(keyCoords, coords);
    } else {
      coords = isGPS ? OlProj.toLonLat(coords) : coords;
      props.onChangeGeometry?.(keyCoords, coords);
    }
  };
  //#endregion

  //#region Functions
  const removeLayer = (layerIdToRemove: string) => {
    const layers = olmap?.getLayers();
    const layerToRemove = layers?.getArray().find(layer => layer.get('id') === layerIdToRemove);

    if (layerToRemove) {
      olmap?.removeLayer(layerToRemove);
    }
  };

  //#region Styles
  const geometryLabel = (type: 'Polygon' | 'Point', name: string) => {
    return new OlText({
      font: '13px Roboto',
      text: name,
      fill: new OlFill({
        color: '#ffffff',
      }),
      backgroundFill: new OlFill({
        color: 'rgba(0, 0, 0, 0.5)',
      }),
      padding: [3, 2, 1, 5],

      textBaseline: 'middle',
      offsetY: type === 'Polygon' ? 0 : 10,
      textAlign: 'center',
    });
  };

  const defaultPolygonStyle = (tGeometry?: CGeometry | null, type = 0) => {
    //type=1=otherGeomtery

    return new OlStyle({
      fill: new OlFill({
        color: '#077bc0' + (type === 1 ? '33' : '50'),
      }),
      stroke: new OlStroke({
        color: theme.palette.primary.main,
        width: type === 1 ? 1 : 3,
      }),
      ...(tGeometry?.name && { text: geometryLabel('Polygon', tGeometry.name) }),
    });
  };

  const defaultPointStyle = (tGeometry: CGeometry, zoom, type = 0) => {
    const getIconScaleForZoom = zoom => {
      return 0.1 + zoom * 0.02;
    };
    const scale = getIconScaleForZoom(zoom);

    //type=1=otherGeomtery
    const iconStyle = new OlStyle({
      image: new OlStyleIcon({
        anchor: [0.5, 1],
        anchorXUnits: 'fraction',
        anchorYUnits: 'fraction',
        src: type === 1 ? otherPoint : newPoint,
        scale: scale,
        // rotation: Math.PI / 4,
      }),
      ...(tGeometry?.name && { text: geometryLabel('Point', tGeometry.name) }),
    });

    /*  const circleStyle = new OlStyle({
      image: new OlCircle({
        radius: 6,
        fill: new OlFill({
          color: theme.palette.primary.light,
        }),
        stroke: new OlStroke({
          color: theme.palette.primary.main,
          width: 3,
        }),
      }),
    });

    const crossStyle = new OlStyle({
      image: new OlStyleRegularShape({
        points: 4,
        radius: 5,
        radius2: 0,
        angle: Math.PI / 4,
        stroke: new OlStroke({
          color: theme.palette.primary.main,
          width: 1,
        }),
      }),
    }); */
    return iconStyle;
  };

  const paintFeature = (feature, type = 0, zoom = 2): OlStyle => {
    const geometry = feature.getGeometry();
    const tGeometry = geometry.get('tGeometry');

    let newStyle: any;
    if (geometry instanceof Point) {
      newStyle = defaultPointStyle(tGeometry, zoom, type);
    } else {
      newStyle = defaultPolygonStyle(tGeometry, type);
    }

    //feature.setStyle();

    return newStyle;
  };
  //#endregion

  //#region Drawer
  const enableInteractions = () => {
    if (drawer.draw && drawer.modify && drawer.translate) {
      log.info('Enable Interactions');
      olmap?.addInteraction(drawer.translate);
      olmap?.addInteraction(drawer.draw);
      olmap?.addInteraction(drawer.modify);
    }
  };

  const disableInteractions = () => {
    if (drawer.draw && drawer.modify && drawer.translate) {
      log.info('Disable Interactions');
      olmap?.removeInteraction(drawer.modify);
      olmap?.removeInteraction(drawer.draw);
      olmap?.removeInteraction(drawer.translate);
    }
  };

  const printCreatedFeature = source => {
    if (!tGeometry) return null;
    let currentFeature: any = null;
    let geometryToEdit: any;
    if (tGeometry.geomGpsCoord && keyCoords === GPS_COORDS) {
      let mapCoordsPolygon: Coordinate[][] = [];
      let mapCoordsPoint: Coordinate = [];

      if (Array.isArray(tGeometry.geomGpsCoord?.coordinates?.[0])) {
        mapCoordsPolygon = [tGeometry.geomGpsCoord.coordinates[0].map(coord => OlProj.fromLonLat(coord))];
      } else {
        mapCoordsPoint = OlProj.fromLonLat([tGeometry.geomGpsCoord.coordinates[0], tGeometry.geomGpsCoord.coordinates[1]]);
      }
      geometryToEdit =
        tGeometry.getTypeDrawer === 'polygon'
          ? new OlFeature(new Polygon(mapCoordsPolygon))
          : new OlFeature(new Point(mapCoordsPoint));
    } else if (tGeometry.geomLocalRectCoord && keyCoords === LOCAL_COORDS) {
      const isArray = Array.isArray(tGeometry.geomLocalRectCoord?.coordinates?.[0]);
      geometryToEdit =
        tGeometry.getTypeDrawer === 'polygon'
          ? new OlFeature(new Polygon(isArray ? tGeometry.geomLocalRectCoord.coordinates : []))
          : new OlFeature(new Point(tGeometry.geomLocalRectCoord.coordinates));
    }
    currentFeature = geometryToEdit;

    geometryToEdit && source.addFeature(geometryToEdit);

    return currentFeature;
  };

  const createDrawer = (
    source,
    currentFeature
  ): { modify: OlInteractionModify; draw: OlInteractionDraw; translate: OlTranslate } => {
    const modify = new OlInteractionModify({
      source,
    });

    modify.on('modifyend', evt => {
      const feature = evt.features.getArray();
      if (!feature || !feature[0]) return;
      const featureGeom = feature[0].getGeometry() as Polygon;
      const coordinates = featureGeom && featureGeom.getCoordinates();
      addOrModifyFeature(coordinates);

      setStateMap({ currentFeature: feature });
    });

    const draw = new OlInteractionDraw({
      source,
      type: tGeometry?.typeDrawer ?? 'Point',
      style: defaultPolygonStyle(),
    });

    draw.on('drawstart', evt => {
      source.clear();
    });

    draw.on('drawend', evt => {
      const feature = evt.feature;
      if (!feature) return;
      const featureGeom = feature.getGeometry() as Polygon;
      const coordinates = featureGeom && featureGeom.getCoordinates();

      addOrModifyFeature(coordinates);
      setStateMap({ currentFeature: feature });
    });

    const translate = new OlTranslate({
      features: new Collection([currentFeature]),
    });

    /* source.on('addfeature', evt => {
      const feature = evt.feature;
      if (!feature) return;
      const featureGeom = feature.getGeometry() as Polygon;
      const coordinates = featureGeom && featureGeom.getCoordinates();

      addOrModifyFeature(coordinates);
      setStateMap({ currentFeature: feature });
    });  */
    return { modify, draw, translate };
  };
  //#endregion

  const onCenterView = () => {
    if (state.tElement) return;
    log.info('Center View');
    if (olmap && stateMap.source && stateMap.currentFeature) {
      const size = olmap.getSize();

      if (size) {
        const geometry = stateMap.currentFeature.getGeometry();

        if (geometry && geometry instanceof Polygon) {
          const extent = stateMap.source.getExtent();
          if (!OlExtent.isEmpty(extent)) {
            const padding = [40, 20, 40, 20];
            olmap.getView()?.fit(extent, { size, padding });
          }
        } else {
          const view = olmap.getView();
          //Point
          view?.fit(geometry);

          let currentZoom = view.getZoom();

          if (currentZoom !== undefined) {
            let newZoom = currentZoom - 2;
            setTimeout(() => view.setZoom(newZoom), 250);
          }
        }
      }
    } else {
      if (state.othersGeometries?.length !== 0) {
        //Center without current feature on otherFeatures
        const existingLayer = olmap
          ?.getLayers()
          .getArray()
          .find((layer): layer is OlLayer<OlSourceVector> => {
            return layer.get('id') === LAYER_OTHER_GEO;
          });

        if (existingLayer) {
          const extent = OlExtent.createEmpty();

          existingLayer
            .getSource()
            ?.getFeatures()
            ?.forEach(feature => {
              const geometry = feature?.getGeometry();
              if (geometry) {
                OlExtent.extend(extent, geometry.getExtent());
              }
            });

          const view = olmap?.getView();

          const isValid = !extent.every(val => val === Infinity || val === -Infinity || isNaN(val));

          if (isValid) view?.fit(extent, { padding: [40, 20, 40, 20] });
        }
      }
    }
  };

  const generateOpenLayerPolygonOrPoint = (geometry: IGeometry, coordinates: Coordinate | Coordinate[]) => {
    if (Array.isArray(coordinates[0])) {
      const newPoly = new Polygon([isGPS ? tList3857(coordinates[0]) : coordinates[0]]);
      newPoly.set('tGeometry', geometry);
      return newPoly;
    } else {
      const newPoint = new Point(t3857(coordinates as number[]));
      newPoint.set('tGeometry', geometry);
      return newPoint;
    }
  };

  const checkCorrectCoordinatesType = (view: CView) => {
    console.log('props', props);
    if (props.mode === 'new' || props.element?.mainFlavor !== 'location') return;
    if (view.viewTypeId === 'map2' && !props.geometry?.geomGpsCoord) {
      setAlert(['Coordinates aren’t compatible with this view.', ' ', 'Switch to the view', ' (' + 'Map' + ').']);
      return;
    }
    if (view.viewTypeId === '2d' && !props.geometry?.geomLocalRectCoord) {
      setAlert(['Coordinates aren’t compatible with this view.', ' ', 'Switch to the view', ' (' + '2D' + ').']);
      return;
    }
    setAlert(null);
  };

  //#region OthersGeometries
  const fetchOthersGeometriesAndGetReadingForElement = async () => {
    const isOutdoor = view?.viewTypeId === 'map2';
    const otherGeo: IGeometry[] = await getGeometries(isOutdoor);
    const firstGeo = otherGeo[0];
    const coordinates = firstGeo?.GPSCentroid?.coordinates;
    const reading = coordinates?.[0] !== undefined && {
      locationGPSLatLonCoordinates: [
        {
          id: 'GPSCentroidLocattion',
          timestamp: new Date().toISOString(),
          data: {
            x: coordinates?.[0],
            y: coordinates?.[1],
          },
          unitId: 'decimalDegrees',
        },
      ],
    };
    if (state.tElement && reading) {
      const newtElement = new CElement(state.tElement, reading);
      setState({ othersGeometries: otherGeo, tElement: newtElement, isLoading: false });
    } else {
      setState({ othersGeometries: otherGeo, isLoading: false });
    }
  };

  const getGeometries = async (isOutdoor?: boolean): Promise<IGeometry[]> => {
    let where = {};

    if (props.othersGeometries) {
      if (props.othersGeometries.length === 0) return [];
      const otherGeomet = await Promise.all(
        props.othersGeometries.map(idLocation =>
          dispatch(fetchData({ url: '/api/v2/location/' + encodeURIComponent(idLocation), dataBody: {}, withoutCancel: true }))
        )
      );

      return otherGeomet.map(geo => geo?.payload?.data).filter(geo => geo) as IGeometry[];
    } else {
      if (isOutdoor) {
        where = {
          geomGpsCoord: {
            $not: null,
          },
          // customId: props.othersGeometries,
          ...(tGeometry?.logicalId && { logicalId: { $ne: tGeometry.logicalId } }),
        };
      } else {
        where = {
          geomLocalRectCoord: {
            $not: null,
          },
          ...(tGeometry?.logicalId && { logicalId: { $ne: tGeometry.logicalId } }),
        };
      }
      const otherGeo: IGeometry[] =
        (
          await dispatch(
            fetchData({
              url: '/api/v2/geometries',
              dataBody: {
                where: where,
              },
            })
          )
        ).payload.data ?? [];
      return otherGeo;
    }
  };

  //#endregion
  //#endregion

  //#region Effects
  const effectPrintOtherGeometriesInMap = (deps: React.DependencyList) => {
    useEffect(() => {
      if (olmap && state.othersGeometries && state.othersGeometries.length > 0) {
        log.info('Effect PrintOtherGeometriesInMap');
        const existingLayer = olmap
          .getLayers()
          .getArray()
          .find(layer => layer.get('id') === LAYER_OTHER_GEO);
        if (existingLayer) {
          olmap.removeLayer(existingLayer);
        }

        const fieldCoord = isGPS ? 'geomGpsCoord' : 'geomLocalRectCoord';

        const geometryList = state.othersGeometries.filter(g => g[fieldCoord]);

        const polygons = geometryList.map(g => generateOpenLayerPolygonOrPoint(g, g[fieldCoord].coordinates));

        const features = polygons.map(polygon => new OlFeature(polygon));

        const vectorLayer = new OlLayerVector({
          source: new OlSourceVector({
            features: features,
          }),
          zIndex: 5,
          style: (feature, resolution) => {
            const zoom = olmap?.getView().getZoomForResolution(resolution);
            return paintFeature(feature, 1, zoom);
          },
        });

        vectorLayer.set('id', LAYER_OTHER_GEO);
        olmap.addLayer(vectorLayer);

        onCenterView();
      }
    }, deps);
  };

  const effectDisabledInteractions = (deps: React.DependencyList) => {
    useEffect(() => {
      if (!props.disabled) {
        enableInteractions();
      } else {
        disableInteractions();
      }
      return () => {
        disableInteractions();
      };
    }, deps);
  };

  const effectCreationGeometryDrawerLayer = (deps: React.DependencyList) => {
    useEffect(() => {
      if (olmap && tGeometry) {
        log.info('Creating geometry drawer');
        const source = new OlSourceVector({ wrapX: false });
        let currentFeature = printCreatedFeature(source);

        const vectorLayer = new OlLayerVector({
          source: source,
          zIndex: 6,
          style: (feature, resolution) => {
            const zoom = olmap?.getView().getZoomForResolution(resolution);
            return paintFeature(feature, 0, zoom);
          },
        });
        vectorLayer.set('id', ID_LAYER_DRAWER);
        removeLayer(ID_LAYER_DRAWER);
        olmap.addLayer(vectorLayer);

        const { modify, draw, translate } = createDrawer(source, currentFeature);
        setDrawer({ modify, draw, translate });

        setStateMap({ source, currentFeature });
      }
    }, deps);
  };

  const effectAfterCreateDrawGeometry = (deps: React.DependencyList) => {
    useEffect(() => {
      const previousValue = previousValueRef.current;
      if (previousValue !== stateMap.source) {
        if (previousValue === null && stateMap.source) {
          onCenterView();
        }
      }
      previousValueRef.current = stateMap.source;
    }, [deps]);
  };

  const effectLoadOtherGeometries = (deps: React.DependencyList) => {
    useEffect(() => {
      if (view) {
        setState({ ...state, isLoading: true });
        fetchOthersGeometriesAndGetReadingForElement();
        checkCorrectCoordinatesType(view);
      }
    }, deps); //view
  };

  //#endregion

  effectCreationGeometryDrawerLayer([olmap, tGeometry?.getTypeDrawer, tGeometry?.geomGpsCoord, tGeometry?.geomLocalRectCoord]);
  effectDisabledInteractions([props.disabled, drawer]);
  effectPrintOtherGeometriesInMap([state.othersGeometries]);
  effectAfterCreateDrawGeometry([stateMap.source]);
  effectLoadOtherGeometries([view]);

  return (
    <div className="relative h-full">
      <QuickMap
        ref={mapRef}
        onOlmapChange={onOlmapChange}
        onCenterView={Boolean(state.othersGeometries && state.othersGeometries?.length > 0) ? onCenterView : undefined}
        isGeometryDrawer={!state.tElement}
        element={state.tElement}
        isLoading={state.isLoading}
        hideInfoSideBar={props.hideInfoSideBar}
        isQuickAccess={props.isQuickAccess}
      />
      <TNotifs
        id="geometryDrawer"
        messageFromParent={alert}
        callbackToClean={() => {
          setAlert(null);
        }}
        containerMode="parent"
        autoHideDuration={10000}
      />
    </div>
  );
};

export default GeometryDrawer;
